import { Badge, ProgressBar } from "react-bootstrap";
import { getRelativeTime } from "../../../../app/dateHelpers";
import { Icon, IconDefinitions } from "../../../../components";
import { GaugeConfig, UploadSessionModel } from "../../../../data/entities";
import { deriveFlwGaugeConfig, getFileName, getProgress } from "../../helper";
import { LiveGauge } from "./LiveGuage";
import { pillBadge } from "./styles";
import { UserService } from "../../../../services";
import { Roles } from "../../../../models";

interface Props {
  session: UploadSessionModel;
  gaugeConfig?: GaugeConfig | null;
}

export function UploadProgressCard({ session, gaugeConfig }: Props) {
  const fileName = getFileName(session.filePath);
  const percent = getProgress(session);
  const isQueued = session.status === "Queued";
  const hasFlawData = session.currentFlawValue != null;
  const hasFlwData = session.currentFlwDifference != null || session.peakFlwDifference != null;
  const flwConfig = hasFlwData ? deriveFlwGaugeConfig(gaugeConfig ?? null) : null;
  const isStaff = UserService.isInRole(Roles.admin) || UserService.isInRole(Roles.staff) || UserService.isInRole(Roles.dataAnalyst);

  return (
    <div className="col-md-12">
      <div
        className="card border-0 h-100"
        style={{
          backgroundColor: "var(--bs-light)",
          transition: "opacity 0.2s ease",
        }}
      >
        <div className="card-body p-3">
          {/* Header */}
          <div className="d-flex align-items-start justify-content-between mb-2">
            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
              <Icon
                iconName={IconDefinitions.fileAlt}
                className={`me-2 ${isQueued ? "text-warning" : "text-primary"} flex-shrink-0`}
              />
              <span className="fw-semibold text-truncate">{fileName}</span>
            </div>
            <div className="d-flex align-items-center flex-shrink-0 ms-2">
              {isQueued ? (
                <Badge bg="warning" style={pillBadge}>
                  Queued
                </Badge>
              ) : (
                <Badge bg="primary" style={pillBadge}>
                  {percent}%
                </Badge>
              )}
            </div>
          </div>

          {/* Inspection info */}
          {session.inspectionId && (
            <div className="small text-muted mb-2">
              <Icon iconName={IconDefinitions.clipboard} className="me-1" />
              {session.inspection.reference}
            </div>
          )}

          {/* Progress bar */}
          <div className="mb-2">
            {isQueued ? (
              <>
                <ProgressBar
                  animated
                  now={100}
                  variant="warning"
                  style={{ height: "6px" }}
                />
                <small className="text-muted mt-1 d-block">
                  Waiting to start...
                </small>
              </>
            ) : (
              <>
                <ProgressBar
                  animated
                  now={percent}
                  variant="primary"
                  style={{ height: "6px" }}
                />
                <div className="d-flex justify-content-between mt-1">
                  {isStaff && session.uploadedChunks > 0 && (
                    <small className="text-muted">
                      {session.uploadedChunks?.toLocaleString()} /{" "}
                      {session.totalChunks?.toLocaleString()} Rows
                    </small>
                  )}
                  {session.lastUploadAt && (
                    <small className="text-muted ms-auto">
                      Last update{" "}
                      {getRelativeTime(new Date(session.lastUploadAt))}
                    </small>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Live flaw + FLW deviation gauges */}
          {!isQueued && hasFlawData && gaugeConfig && (
            <div className="border-top pt-3 mt-2">
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <div className="w-100" style={{ maxWidth: 300 }}>
                  <div
                    className="text-uppercase fw-semibold mb-1 text-muted text-center"
                    style={{ fontSize: "0.6875rem", letterSpacing: "0.08em" }}
                  >
                    Live Defect Rate
                  </div>
                  <LiveGauge
                    currentFlawValue={session.currentFlawValue ?? 0}
                    peakFlawValue={session.peakFlawValue}
                    config={gaugeConfig}
                  />
                </div>
                {flwConfig && (
                  <div className="w-100" style={{ maxWidth: 300 }}>
                    <div
                      className="text-uppercase fw-semibold mb-1 text-muted text-center"
                      style={{ fontSize: "0.6875rem", letterSpacing: "0.08em" }}
                    >
                      Rolling Avg Deviation
                    </div>
                    <LiveGauge
                      currentFlawValue={session.currentFlwDifference}
                      peakFlawValue={session.peakFlwDifference}
                      config={flwConfig}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
            <small className="text-muted">
              <Icon iconName={IconDefinitions.clock} className="me-1" />
              Started {getRelativeTime(new Date(session.startDate))}
            </small>
            <small className="text-muted">
              {(session.fileSizeBytes / 1024 / 1024).toFixed(1)} MB
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}
